/**
 * Which fields a redemption rule shows, checks and sends, by type.
 *
 * Mirrors `LoyaltyRedemptionConfigSerializer.TYPE_FIELDS` on the backend, which
 * clears whatever a type does not use. Purchase Discount keeps every field it
 * always had; the other types only get the fields checkout actually reads:
 *
 *   PURCHASE_DISCOUNT  max points, max discount %, max ₹ cap
 *   DELIVERY           max ₹ waived (the rule costs exactly its points)
 *   PRODUCT            which loyalty product (an offer of type "loyalty offer"
 *                      from Offer Management; the server takes the product
 *                      from it), max ₹ value
 *   OFFER              which offer
 *
 * The three rewards also take an optional validity window and redemption
 * limits. Blank means "always" and "unlimited", which is how every existing
 * rule behaves. Purchase Discount never reads them.
 */

/** Optional validity window and limits, for the non-discount rewards. */
export const REWARD_LIMIT_FIELDS = [
  'valid_from',
  'valid_until',
  'max_redemptions_total',
  'max_redemptions_per_user',
];

export const REDEMPTION_TYPES = [
  { value: 'OFFER', label: 'Offer Redemption' },
  { value: 'PURCHASE_DISCOUNT', label: 'Purchase Discount' },
  { value: 'DELIVERY', label: 'Free Delivery' },
  { value: 'PRODUCT', label: 'Free Product' },
];

export const TYPE_FIELDS = {
  PURCHASE_DISCOUNT: ['max_points_allowed', 'max_discount_percentage', 'max_redemption_value'],
  DELIVERY: ['max_redemption_value', ...REWARD_LIMIT_FIELDS],
  PRODUCT: ['offer', 'max_redemption_value', ...REWARD_LIMIT_FIELDS],
  OFFER: ['offer', ...REWARD_LIMIT_FIELDS],
};

const OPTIONAL_FIELDS = [
  'max_points_allowed',
  'max_discount_percentage',
  'max_redemption_value',
  'product',
  'offer',
  ...REWARD_LIMIT_FIELDS,
];

export const TYPE_SUBTITLES = {
  PURCHASE_DISCOUNT: 'Points required and discount limits for checkout redemption',
  DELIVERY: 'Points a customer spends to waive the delivery charge',
  PRODUCT: 'Points a customer spends to get a product free',
  OFFER: 'Points a customer spends to unlock an offer',
};

/** Labels for the ₹ cap, which means something different per type. */
export const REDEMPTION_VALUE_LABELS = {
  PURCHASE_DISCOUNT: {
    label: 'Max redemption value',
    helperText: '₹ cap on the discount. Leave blank for no cap.',
  },
  DELIVERY: {
    label: 'Max delivery waiver (₹)',
    helperText: 'Most of the delivery charge this covers. Leave blank to cover it all.',
  },
  PRODUCT: {
    label: 'Max product value (₹)',
    helperText: 'Most the free product may be worth. Leave blank for no cap.',
  },
};

export const emptyRedemptionForm = {
  loyalty_config: '',
  redemption_type: 'PURCHASE_DISCOUNT',
  name: '',
  description: '',
  min_points_required: '100',
  max_points_allowed: '',
  max_discount_percentage: '',
  max_redemption_value: '',
  product: '',
  offer: '',
  valid_from: '',
  valid_until: '',
  max_redemptions_total: '',
  max_redemptions_per_user: '',
  is_active: true,
};

export const usesField = (type, field) => (TYPE_FIELDS[type] || []).includes(field);

/** Switch type, blanking every field the new type does not use. */
export const changeRedemptionType = (form, type) => {
  const next = { ...form, redemption_type: type };
  OPTIONAL_FIELDS.forEach((field) => {
    if (!usesField(type, field)) next[field] = emptyRedemptionForm[field];
  });
  return next;
};

const isBlank = (v) => v === '' || v === null || v === undefined;
const isWholeNumber = (v) => /^\d+$/.test(String(v).trim());

/** Field -> message, for the fields that apply to the selected type only. */
export const validateRedemptionForm = (form) => {
  const errors = {};
  const type = form.redemption_type;

  if (!form.loyalty_config) errors.loyalty_config = 'Select a loyalty config';
  if (!REDEMPTION_TYPES.some((t) => t.value === type)) errors.redemption_type = 'Select a type';
  if (!String(form.name || '').trim()) errors.name = 'Name is required';

  // Purchase Discount has always accepted 0 (the backend falls back to its
  // default); every other type is refused at checkout without a cost.
  const minPoints = form.min_points_required;
  const minRequired = type === 'PURCHASE_DISCOUNT' ? 0 : 1;
  if (isBlank(minPoints) || !isWholeNumber(minPoints)) {
    errors.min_points_required = 'Enter a whole number of points';
  } else if (Number(minPoints) < minRequired) {
    errors.min_points_required = 'Points required must be at least 1';
  }

  if (usesField(type, 'max_points_allowed') && !isBlank(form.max_points_allowed)) {
    if (!isWholeNumber(form.max_points_allowed)) {
      errors.max_points_allowed = 'Enter a whole number of points';
    } else if (
      !errors.min_points_required &&
      Number(form.max_points_allowed) > 0 &&
      Number(form.max_points_allowed) < Number(minPoints)
    ) {
      errors.max_points_allowed = 'Cannot be less than min points required';
    }
  }

  if (usesField(type, 'max_discount_percentage') && !isBlank(form.max_discount_percentage)) {
    const pct = Number(form.max_discount_percentage);
    if (Number.isNaN(pct) || pct <= 0 || pct > 100) {
      errors.max_discount_percentage = 'Enter a percentage between 0 and 100';
    }
  }

  if (usesField(type, 'max_redemption_value') && !isBlank(form.max_redemption_value)) {
    const cap = Number(form.max_redemption_value);
    if (Number.isNaN(cap) || cap <= 0) errors.max_redemption_value = 'Enter an amount above 0';
  }

  ['max_redemptions_total', 'max_redemptions_per_user'].forEach((field) => {
    if (usesField(type, field) && !isBlank(form[field])) {
      if (!isWholeNumber(form[field]) || Number(form[field]) < 1) {
        errors[field] = 'Enter a whole number of at least 1';
      }
    }
  });

  if (usesField(type, 'valid_until') && !isBlank(form.valid_from) && !isBlank(form.valid_until)) {
    if (new Date(form.valid_until) <= new Date(form.valid_from)) {
      errors.valid_until = 'Must be after Valid from';
    }
  }

  if (usesField(type, 'offer') && isBlank(form.offer)) {
    errors.offer = type === 'PRODUCT' ? 'Select a loyalty product' : 'Select an offer';
  }
  if (usesField(type, 'product') && isBlank(form.product)) errors.product = 'Select a product';

  return errors;
};

/** The request body; fields the type does not use are sent as null. */
export const buildRedemptionPayload = (form) => {
  const type = form.redemption_type;
  const numOrNull = (field) =>
    usesField(type, field) && !isBlank(form[field]) ? Number(form[field]) : null;

  // `datetime-local` gives local wall-clock time; the API stores an instant.
  const instantOrNull = (field) =>
    usesField(type, field) && !isBlank(form[field]) ? new Date(form[field]).toISOString() : null;

  // Only sent for the types that use them, so a Purchase Discount request is
  // exactly what it has always been.
  const rewardLimits = usesField(type, 'valid_from')
    ? {
        valid_from: instantOrNull('valid_from'),
        valid_until: instantOrNull('valid_until'),
        max_redemptions_total: numOrNull('max_redemptions_total'),
        max_redemptions_per_user: numOrNull('max_redemptions_per_user'),
      }
    : {};

  return {
    loyalty_config: Number(form.loyalty_config),
    redemption_type: type,
    name: form.name.trim(),
    description: form.description || '',
    min_points_required: Number(form.min_points_required || 0),
    max_points_allowed: numOrNull('max_points_allowed'),
    max_discount_percentage: numOrNull('max_discount_percentage'),
    max_redemption_value: numOrNull('max_redemption_value'),
    product: numOrNull('product'),
    offer: numOrNull('offer'),
    ...rewardLimits,
    is_active: !!form.is_active,
  };
};

/** An API datetime as the `YYYY-MM-DDTHH:mm` a datetime-local input shows. */
export const toLocalInput = (value) => {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

/** A saved rule, as form state. */
export const redemptionToForm = (item) => {
  const str = (v) => (v != null ? String(v) : '');
  return {
    loyalty_config: item.loyalty_config ?? '',
    redemption_type: item.redemption_type || 'PURCHASE_DISCOUNT',
    name: item.name || '',
    description: item.description || '',
    min_points_required: String(item.min_points_required ?? '100'),
    max_points_allowed: str(item.max_points_allowed),
    max_discount_percentage: str(item.max_discount_percentage),
    max_redemption_value: str(item.max_redemption_value),
    product: item.product ?? '',
    offer: item.offer ?? '',
    valid_from: toLocalInput(item.valid_from),
    valid_until: toLocalInput(item.valid_until),
    max_redemptions_total: str(item.max_redemptions_total),
    max_redemptions_per_user: str(item.max_redemptions_per_user),
    is_active: item.is_active !== false,
  };
};

/** DRF field errors ({offer: ["..."]}) as a flat field -> message map. */
export const apiFieldErrors = (data) => {
  if (!data || typeof data !== 'object' || Array.isArray(data)) return {};
  const out = {};
  Object.entries(data).forEach(([field, value]) => {
    const msg = Array.isArray(value) ? value[0] : value;
    if (typeof msg === 'string') out[field] = msg;
  });
  return out;
};
